import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import { Formik } from 'formik';
import { Button, TextField, Link, Typography } from '@material-ui/core';
import * as Yup from 'yup';

import logo from '../assets/logo';
import RecaptchaLegal from '../common/RecaptchaLegal';
import { useLogin, LoginRequest } from '../user/User.hooks';
import * as Styled from './Login.styled';
import { useLoginRedirect } from './Login.context';
import JiraLogin from './JiraLogin';
import { authConfig } from '../secure-portal/auth/Auth.config';
import { AuthMethodId } from '../secure-portal/auth/Auth.interface';
import AuthMethodListPicker from '../secure-portal/auth/components/AuthMethodListPicker';
import { getGoogleAuth } from '../secure-portal/google/GoogleAuth';
import { useGoogleLogin } from '../secure-portal/google/GoogleAuth.hooks';
import JiraServerLogin from '../secure-portal/jira-server/JiraServerLogin';
import { useOktaLogin } from '../secure-portal/okta/Okta.hooks';
import JiraEmailLogin from './JiraEmailLogin';
import { User } from '../user/User.interface';
import { useRemoveJiraWidget } from '../common/useRemoveJiraWidget';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid e-mail address.')
    .required('E-mail address is required.'),
  password: Yup.string().required('Password is required.')
});

const Login: React.FC<RouteComponentProps> = (props: RouteComponentProps) => {
  useRemoveJiraWidget();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [currentUser, setCurrentUser] = useState<User>();
  const [jiraEmail, setJiraEmail] = useState<string | undefined>();
  const [requestEmail, setRequestEmail] = useState(false);
  const [selectedAuthMethodId, setSelectedAuthMethodId] = useState(
    authConfig.defaultMethod.id
  );
  const { user, isLoading: isLoadingLogin, error, doLogin } = useLogin();
  const { isLoading: isLoadingGoogleLogin, login: googleLogin } =
    useGoogleLogin();
  const { isLoading: isLoadingOktaLogin, login: oktaLogin } = useOktaLogin();
  const { loginRedirect, didSessionExpire } = useLoginRedirect();
  const isLoading =
    isLoadingLogin || isLoadingGoogleLogin || isLoadingOktaLogin;
  const isGoogleAuth = selectedAuthMethodId === AuthMethodId.Google;

  useEffect(() => {
    if (error) {
      setErrorMessage(
        error.message === 'Unauthorized'
          ? 'The Email ID / Password combination is incorrect. Please try again or contact your site administrator'
          : error.message
      );
    }
    if (user) {
      setCurrentUser(user);
    }
  }, [user, error]);

  useLayoutEffect(() => {
    // render the button under the .auth-method-area element
    const parent = containerRef.current?.querySelector<HTMLElement>(
      `.auth-method-list-picker [data-auth-method="${AuthMethodId.Google}"] .auth-method-area`
    );
    if (!parent) {
      return;
    }
    (async () => {
      const googleAuth = await getGoogleAuth();
      googleAuth.renderButton({
        parent,
        onClick: () => {
          setErrorMessage(undefined);
          setSelectedAuthMethodId(AuthMethodId.Google);
        },
        onResponse: async response => {
          const { error } = await googleLogin({ token: response.credential });
          error && setErrorMessage(error.message);
        }
      });
    })();
  }, [googleLogin]);

  if (currentUser) {
    if (currentUser && currentUser.roles) {
      if (loginRedirect) {
        return <Redirect to={loginRedirect} />;
      }
    } else {
      setErrorMessage('You do not have sufficient permissions.');
    }
  }

  return (
    <Styled.StyledLoginContainer ref={containerRef}>
      <Styled.StyledLoginCard>
        <img src={logo} alt='Sierra Labs' className='logo' />
        <h2 style={authConfig.multiple ? { marginBottom: 16 } : undefined}>
          Log In
        </h2>
        {authConfig.multiple && (
          <AuthMethodListPicker
            label='Log in using:'
            disabled={isLoading}
            value={selectedAuthMethodId}
            style={{ marginBottom: 16 }}
            onChange={authMethod => {
              if (!authMethod) {
                return;
              }
              setSelectedAuthMethodId(authMethod.id);
              if (authMethod.id !== selectedAuthMethodId) {
                // remove errors when switching auth methods
                setErrorMessage(undefined);
              }
            }}
          />
        )}

        {requestEmail ? (
          <JiraEmailLogin errorMessage={errorMessage} onChange={setJiraEmail} />
        ) : (
          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={loginSchema}
            validateOnBlur={true}
            onSubmit={async (form: LoginRequest) => {
              setErrorMessage(undefined);
              const id = selectedAuthMethodId || authConfig.defaultMethod.id;
              switch (id) {
                case AuthMethodId.Okta: {
                  const { error } = await oktaLogin({
                    email: form.email,
                    password: form.password
                  });
                  error && setErrorMessage(error.message);
                  break;
                }
                default:
                  doLogin(form.email, form.password);
              }
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur }) => (
              <Styled.StyledLoginForm>
                {isGoogleAuth ? (
                  <Typography variant='body1'>
                    {isLoading
                      ? 'Loading...'
                      : 'Select your Google account from the separate window to login.'}
                  </Typography>
                ) : (
                  <>
                    <TextField
                      variant='outlined'
                      type='email'
                      name='email'
                      label='Email'
                      className='form-field'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      error={touched.email && !!errors.email}
                      helperText={errors.email}
                    />
                    <TextField
                      variant='outlined'
                      type='password'
                      name='password'
                      label='Password'
                      className='form-field'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      error={touched.password && !!errors.password}
                      helperText={errors.password}
                    />
                  </>
                )}

                {didSessionExpire ? (
                  <p className='error'>
                    {
                      'You were logged out of the QMS due to inactivity. Please log back into the system to resume your work.'
                    }
                  </p>
                ) : (
                  !isLoading &&
                  errorMessage && <p className='error'>{errorMessage}</p>
                )}
                {!isGoogleAuth && (
                  <Button
                    variant='contained'
                    disableElevation
                    type='submit'
                    disabled={isLoading}
                  >
                    Log In
                  </Button>
                )}
                <RecaptchaLegal style={{ marginTop: 10 }} />
              </Styled.StyledLoginForm>
            )}
          </Formik>
        )}

        {requestEmail ? (
          <Link
            key='back'
            className='forgot-password-link'
            onClick={() => {
              setJiraEmail('');
              setRequestEmail(false);
            }}
          >
            Cancel
          </Link>
        ) : (
          <Link className='forgot-password-link' href='/password/recover'>
            Forgot Password?
          </Link>
        )}
        <JiraServerLogin
          {...props}
          onError={err => {
            console.debug(err);
            setErrorMessage(
              'Error establishing authorization with JIRA, please ensure that the Application Link is properly configured.'
            );
          }}
        />
        <JiraLogin
          email={jiraEmail}
          disabled={requestEmail && !jiraEmail}
          onError={err => {
            console.debug(err);
            if (err?.statusCode === 422 && err?.error === 'No Email Provided') {
              setRequestEmail(true);
            }
            setErrorMessage(err.message);
          }}
          onLogin={user => {
            console.debug(user);
            setCurrentUser(user);
          }}
        />
      </Styled.StyledLoginCard>
    </Styled.StyledLoginContainer>
  );
};

export default withRouter(Login);
